import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Target,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Activity,
  Ruler,
  Plus,
  Edit2,
  Scale,
  X,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from "recharts";

// ─── helpers ────────────────────────────────────────────────────────────────
const GOAL_TYPE_COLORS = {
  weight_loss: { bg: "bg-blue-500/10", text: "text-blue-500" },
  muscle_gain: { bg: "bg-purple-500/10", text: "text-purple-500" },
  endurance: { bg: "bg-orange-500/10", text: "text-orange-500" },
  flexibility: { bg: "bg-pink-500/10", text: "text-pink-500" },
  general_fitness: { bg: "bg-teal-500/10", text: "text-teal-500" },
  default: { bg: "bg-primary/10", text: "text-primary" },
};

const MEASUREMENT_INFO = {
  weight: { label: "Weight", unit: "kg" },
  chest: { label: "Chest", unit: "cm" },
  waist: { label: "Waist", unit: "cm" },
  arm: { label: "Arm", unit: "cm" },
  bmi: { label: "BMI", unit: "" },
};

function goalColor(type) {
  return GOAL_TYPE_COLORS[type] || GOAL_TYPE_COLORS.default;
}

function ProgressBar({ value, max = 100, color = "bg-primary" }) {
  const pct = Math.min(Math.max((value / Math.max(max, 1)) * 100, 0), 100);
  return (
    <div className="h-2 bg-secondary rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-2.5 sm:p-4">
        <div className="flex items-center justify-between gap-1 sm:items-start">
          <div className="w-full">
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 sm:mb-1 truncate">
                {label}
              </p>
              <div className={` bg-secondary/30 sm:bg-transparent ${color}`}>
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </div>
            </div>
            <p
              className={`text-lg sm:text-2xl font-black font-heading leading-none ${color}`}
            >
              {value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Mini inline sparkline (SVG) for measurement trend
function Sparkline({ values, color = "#6366f1" }) {
  if (!values || values.length < 2) return null;
  const W = 80,
    H = 28,
    pad = 4;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg width={W} height={H} className="shrink-0">
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle
        cx={pts[pts.length - 1].split(",")[0]}
        cy={pts[pts.length - 1].split(",")[1]}
        r="2.5"
        fill={color}
      />
    </svg>
  );
}

// ─── Local Smart Coach analysis engine ───────────────────────────────────────
function calculateLocalSmartInsights(goal, workoutData) {
  if (!goal) return [];
  const entries = goal.entries || [];

  // Find weight or first tracked metric
  const measurement = goal.measurements?.[0];
  if (!measurement) return [];
  const metric = measurement.metric;

  const startVal = parseFloat(measurement.currentValue);
  const targetVal = parseFloat(measurement.targetValue);
  if (isNaN(startVal) || isNaN(targetVal)) return [];

  // Extract all values for this metric in chronological order
  const logs = [];
  const goalStartDate = goal.startDate ? new Date(goal.startDate) : null;
  if (goalStartDate) {
    logs.push({ date: goalStartDate, val: startVal });
  }

  const sortedEntries = [...entries].sort((a, b) => {
    const da = a.recordedDate || a.date || a.createdAt || "";
    const db = b.recordedDate || b.date || b.createdAt || "";
    return da.localeCompare(db);
  });

  sortedEntries.forEach((e) => {
    const pm = e.measurements?.find((p) => p.metric === metric);
    if (pm && !isNaN(parseFloat(pm.value))) {
      logs.push({
        date: new Date(e.recordedDate || e.date || e.createdAt),
        val: parseFloat(pm.value),
      });
    }
  });

  if (logs.length === 0) return [];

  const latestLog = logs[logs.length - 1];
  const latestVal = latestLog.val;
  const isWeightLoss = targetVal < startVal;
  const totalChange = latestVal - startVal;
  const unit = metric === "weight" ? "kg" : "cm";

  const insights = [];

  // 1. Scenario A: Weight regain/relapse warning
  let bestVal = startVal;
  let bestLogIndex = 0;
  logs.forEach((l, idx) => {
    if (isWeightLoss) {
      if (l.val < bestVal) {
        bestVal = l.val;
        bestLogIndex = idx;
      }
    } else {
      if (l.val > bestVal) {
        bestVal = l.val;
        bestLogIndex = idx;
      }
    }
  });

  const hasRegained = isWeightLoss
    ? latestVal - bestVal > 0.5
    : bestVal - latestVal > 0.5;

  if (hasRegained) {
    const formattedBestDate = logs[bestLogIndex].date.toLocaleDateString(
      "en-IN",
      {
        day: "numeric",
        month: "short",
      },
    );
    insights.push({
      type: "regain",
      title: "Consistency Alert",
      message: `You achieved a great milestone of ${bestVal} ${unit} on ${formattedBestDate}. However, your latest check-in is ${latestVal} ${unit}. Routine and consistency are key to locking in your achievements!`,
      status: "warning",
      icon: "AlertTriangle",
    });
  }

  // 2. Scenario B: Goal projection
  const firstLog = logs[0];
  const timeDiff = latestLog.date - firstLog.date;
  const daysElapsed = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  const improved = isWeightLoss ? latestVal < startVal : latestVal > startVal;

  if (daysElapsed >= 7 && improved) {
    const ratePerDay = Math.abs(totalChange) / daysElapsed;
    const ratePerWeek = ratePerDay * 7;
    const remainingToGoal = Math.abs(targetVal - latestVal);

    if (ratePerDay > 0.01) {
      const daysToGoal = remainingToGoal / ratePerDay;
      const weeksToGoal = Math.ceil(daysToGoal / 7);
      const projectedDate = new Date(
        latestLog.date.getTime() + daysToGoal * 24 * 3600 * 1000,
      );
      const formattedProjDate = projectedDate.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

      insights.push({
        type: "projection",
        title: "Goal Projection",
        message: `At your current progress rate of ${ratePerWeek.toFixed(2)} ${unit}/week, you are projected to reach your target of ${targetVal} ${unit} in about ${weeksToGoal} weeks (around ${formattedProjDate}). Keep pushing!`,
        status: "success",
        icon: "Sparkles",
      });
    }
  }

  // 3. Scenario C: Missed workouts (leave days) impact
  if (workoutData && workoutData.schedule) {
    const schedules = workoutData.schedule || [];
    const todayStr = new Date().toISOString().slice(0, 10);

    const pastNonRest = schedules.filter(
      (s) => s.date <= todayStr && s.phase !== "rest",
    );
    const completedWorkouts = pastNonRest.filter(
      (s) => s.status === "completed",
    );
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const oneWeekAgoStr = oneWeekAgo.toISOString().slice(0, 10);
    const missedWorkoutsThisWeek = pastNonRest.filter(
      (s) =>
        s.date >= oneWeekAgoStr &&
        (s.status === "missed" || s.status === "skipped"),
    );
    const missedWorkoutsAll = pastNonRest.filter(
      (s) => s.status === "missed" || s.status === "skipped",
    );

    if (missedWorkoutsThisWeek.length > 0) {
      insights.push({
        type: "leave",
        title: "Attendance Impact",
        message: `You missed ${missedWorkoutsThisWeek.length} scheduled ${missedWorkoutsThisWeek.length === 1 ? "workout" : "workouts"} this week. Completing them may have improved consistency and accelerated progress.`,
        status: "info",
        icon: "Clock",
      });
    } else if (missedWorkoutsAll.length > 0) {
      insights.push({
        type: "leave",
        title: "Attendance Impact",
        message: `You missed ${missedWorkoutsAll.length} scheduled ${missedWorkoutsAll.length === 1 ? "workout" : "workouts"} so far. Completing your upcoming sessions will help build consistency and accelerate progress.`,
        status: "info",
        icon: "Clock",
      });
    }

    // 4. Scenario D & E: Plateau warning
    if (pastNonRest.length >= 6) {
      const completionRate = completedWorkouts.length / pastNonRest.length;

      // Filter logs in the last 21 days
      const threeWeeksAgo = new Date();
      threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
      const recentLogs = logs.filter((l) => l.date >= threeWeeksAgo);

      if (completionRate >= 0.7 && recentLogs.length >= 2) {
        const minVal = Math.min(...recentLogs.map((l) => l.val));
        const maxVal = Math.max(...recentLogs.map((l) => l.val));
        const diff = maxVal - minVal;

        if (diff <= 0.3) {
          insights.push({
            type: "plateau",
            title: "Plateau Warning",
            message: `You are doing great! Your workout attendance is a solid ${(completionRate * 100).toFixed(0)}%, but your weight has stabilized at ${latestVal} ${unit}. To break this plateau, try switching your workout intensity or consulting your coach for a routine modification.`,
            status: "warning",
            icon: "TrendingUp",
          });
        }
      }
    }
  }

  return insights;
}

function ProgressInsightsSection({ goal, workoutData }) {
  const insights = useMemo(() => {
    return calculateLocalSmartInsights(goal, workoutData);
  }, [goal, workoutData]);

  // Calculations for Coach Cards
  const stats = useMemo(() => {
    if (!goal) return null;
    const entries = goal.entries || [];

    // Find primary metric
    const primaryMeasurement = goal.measurements?.[0];
    const primaryMetric = primaryMeasurement?.metric;
    const primaryStart = primaryMeasurement
      ? parseFloat(primaryMeasurement.currentValue)
      : null;
    const primaryTarget = primaryMeasurement
      ? parseFloat(primaryMeasurement.targetValue)
      : null;
    const primaryUnit = MEASUREMENT_INFO[primaryMetric]?.unit || "";

    // Sort entries
    const sorted = [...entries].sort((a, b) => {
      const da = a.recordedDate || a.date || a.createdAt || "";
      const db = b.recordedDate || b.date || b.createdAt || "";
      return da.localeCompare(db);
    });

    const primaryLatest =
      sorted.length > 0
        ? sorted[sorted.length - 1].measurements?.find(
            (p) => p.metric === primaryMetric,
          )?.value || primaryStart
        : primaryStart;

    // Pace & Projected Goal Date
    let weeklyPace = "—";
    let projectedGoalDate = goal.targetDate
      ? new Date(goal.targetDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—";

    const logs = [];
    const goalStartDate = goal.startDate ? new Date(goal.startDate) : null;
    if (goalStartDate && primaryStart !== null) {
      logs.push({ date: goalStartDate, val: primaryStart });
    }
    sorted.forEach((e) => {
      const pm = e.measurements?.find((p) => p.metric === primaryMetric);
      if (pm && !isNaN(parseFloat(pm.value))) {
        logs.push({
          date: new Date(e.recordedDate || e.date || e.createdAt),
          val: parseFloat(pm.value),
        });
      }
    });

    if (logs.length >= 2 && primaryStart !== null && primaryTarget !== null) {
      const latestLog = logs[logs.length - 1];
      const firstLog = logs[0];
      const timeDiff = latestLog.date - firstLog.date;
      const daysElapsed = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      const isWeightLoss = primaryTarget < primaryStart;
      const improved = isWeightLoss
        ? latestLog.val < primaryStart
        : latestLog.val > primaryStart;

      if (daysElapsed >= 7 && improved) {
        const totalChange = latestLog.val - firstLog.val;
        const ratePerDay = Math.abs(totalChange) / daysElapsed;
        const ratePerWeek = ratePerDay * 7;
        if (ratePerWeek > 0.01) {
          weeklyPace = `${ratePerWeek.toFixed(1)} ${primaryUnit}/wk`;
          const remainingToGoal = Math.abs(primaryTarget - latestLog.val);
          const daysToGoal = remainingToGoal / ratePerDay;
          const projDate = new Date(
            latestLog.date.getTime() + daysToGoal * 24 * 3600 * 1000,
          );
          projectedGoalDate = projDate.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }
      }
    }

    // Workout consistency
    const schedules = workoutData?.schedule || [];
    const todayStr = new Date().toISOString().slice(0, 10);
    const pastNonRest = schedules.filter(
      (s) => s.date <= todayStr && s.phase !== "rest",
    );
    const completed = pastNonRest.filter(
      (s) => s.status === "completed",
    ).length;
    const consistencyScore =
      pastNonRest.length > 0
        ? `${Math.round((completed / pastNonRest.length) * 100)}%`
        : "—";

    return {
      projectedGoalDate,
      missed: pastNonRest.filter(
        (s) => s.status === "missed" || s.status === "skipped",
      ).length,
      consistencyScore,
      weeklyPace,
    };
  }, [goal, workoutData]);

  if (!goal) return null;

  return (
    <Card className="bg-gradient-to-br from-card/90 via-card to-secondary/10 border-border/80 shadow-lg overflow-hidden relative">
      <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full filter blur-3xl pointer-events-none" />

      <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-black uppercase tracking-wider flex items-center gap-2 text-primary">
          <Sparkles className="h-4.5 w-4.5 animate-pulse text-primary" />
          Smart Coach Insights
        </CardTitle>
        <Badge
          variant="outline"
          className="bg-secondary/40 border-border/60 text-[9px] font-mono font-medium tracking-tight px-1.5 py-0.5 select-none uppercase animate-pulse text-primary border-primary/20 hidden"
        >
          AI Advisor Active
        </Badge>
      </CardHeader>

      <CardContent className="p-4 pt-1 space-y-4">
        {/* Scannable Grid Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="bg-background/45 backdrop-blur-sm border border-border/30 p-2.5 rounded-xl text-center flex flex-col justify-center select-none shadow-sm transition-all hover:border-primary/25">
              <span className="text-[14px] mb-2">🎯</span>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                Goal Date
              </p>
              <p className="text-xs  font-semibold mt-2 text-foreground">
                {stats.projectedGoalDate}
              </p>
            </div>
            <div className="bg-background/45 backdrop-blur-sm border border-border/30 p-2.5 rounded-xl text-center flex flex-col justify-center select-none shadow-sm transition-all hover:border-red-500/25">
              <span className="text-[14px] mb-2">🏋️</span>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                Missed Workouts
              </p>
              <p
                className={`text-xs font-black font-heading mt-2 ${stats.missed > 0 ? "text-red-500 font-bold" : "text-muted-foreground"}`}
              >
                {stats.missed} {stats.missed === 1 ? "session" : "sessions"}
              </p>
            </div>
            <div className="bg-background/45 backdrop-blur-sm border border-border/30 p-2.5 rounded-xl text-center flex flex-col justify-center select-none shadow-sm transition-all hover:border-green-500/25">
              <span className="text-[14px] mb-2">⚡</span>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                Consistency Score
              </p>
              <p className="text-xs font-black font-heading mt-2 text-green-500">
                {stats.consistencyScore}
              </p>
            </div>
            <div className="bg-background/45 backdrop-blur-sm border border-border/30 p-2.5 rounded-xl text-center flex flex-col justify-center select-none shadow-sm transition-all hover:border-primary/25">
              <span className="text-[14px] mb-2">📈</span>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                Weekly Pace
              </p>
              <p className="text-xs font-black font-heading mt-2 text-primary">
                {stats.weeklyPace}
              </p>
            </div>
          </div>
        )}

        {/* Dynamic Coach Recommendations Text List */}
        {insights && insights.length > 0 && (
          <div className="space-y-2.5 pt-2.5 border-t border-border/10">
            {insights.map((insight, idx) => {
              let statusBg = "bg-primary/5 border-primary/20";
              let textColor = "text-foreground";
              let IconComp = Sparkles;

              if (insight.status === "warning") {
                statusBg = "bg-amber-500/5 border-amber-500/20";
                textColor = "text-amber-500 dark:text-amber-400";
                IconComp = AlertTriangle;
              } else if (insight.status === "success") {
                statusBg = "bg-green-500/5 border-green-500/20";
                textColor = "text-green-500 dark:text-green-400";
                IconComp = Sparkles;
              } else if (insight.status === "info") {
                statusBg = "bg-blue-500/5 border-blue-500/20";
                textColor = "text-blue-500 dark:text-blue-400";
                IconComp = Clock;
              }

              if (insight.icon === "TrendingUp") {
                IconComp = TrendingUp;
              }

              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${statusBg} transition-all duration-300 hover:shadow-sm`}
                >
                  <div
                    className={`p-1.5 rounded-lg bg-background/50 border border-border/40 ${textColor} shrink-0 mt-0.5`}
                  >
                    <IconComp className="h-4 w-4" />
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <p
                      className={`text-xs font-bold font-mono font-heading uppercase tracking-wider leading-none ${textColor}`}
                    >
                      {insight.title}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                      {insight.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── ProgressMetricRow ────────────────────────────────────────────────────────
function ProgressMetricRow({
  m,
  vals,
  latestValue,
  isImproved,
  diff,
  info,
  memberHeight,
  entries,
  startDate,
}) {
  const [animatedPct, setAnimatedPct] = useState(0);

  const startVal = parseFloat(m.currentValue) || 0;
  const currentVal = parseFloat(latestValue) || 0;
  const targetVal = parseFloat(m.targetValue) || 0;

  const totalDiff = targetVal - startVal;
  const currentDiff = currentVal - startVal;
  let pct = 0;
  if (totalDiff !== 0) {
    pct = Math.min(Math.max((currentDiff / totalDiff) * 100, 0), 100);
  } else if (currentVal === targetVal) {
    pct = 100;
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setAnimatedPct(pct);
    }, 100);
    return () => clearTimeout(t);
  }, [pct]);

  const isWeight = m.metric === "weight";
  let bmiInfo = null;
  if (isWeight && memberHeight) {
    const heightNum = parseFloat(memberHeight);
    if (heightNum > 0 && startVal > 0 && currentVal > 0) {
      const hM = heightNum / 100;
      const startBmi = (startVal / (hM * hM)).toFixed(1);
      const currentBmi = (currentVal / (hM * hM)).toFixed(1);
      const targetBmi =
        targetVal > 0 ? (targetVal / (hM * hM)).toFixed(1) : null;

      let category = "";
      let categoryColor = "";
      let categoryBg = "";
      let bmiPct = 0;
      const bmiVal = parseFloat(currentBmi);

      if (bmiVal < 18.5) {
        category = "Underweight";
        categoryColor = "text-blue-500 dark:text-blue-400";
        categoryBg = "bg-blue-500/10";
      } else if (bmiVal < 25.0) {
        category = "Normal";
        categoryColor = "text-green-500 dark:text-green-400";
        categoryBg = "bg-green-500/10";
      } else if (bmiVal < 30.0) {
        category = "Overweight";
        categoryColor = "text-amber-500 dark:text-amber-400";
        categoryBg = "bg-amber-500/10";
      } else {
        category = "Obese";
        categoryColor = "text-red-500 dark:text-red-400";
        categoryBg = "bg-red-500/10";
      }

      bmiPct = Math.min(Math.max(((bmiVal - 15) / (35 - 15)) * 100, 0), 100);

      const bmiStartNum = parseFloat(startBmi) || 0;
      const bmiCurrentNum = parseFloat(currentBmi) || 0;
      const bmiDiff = bmiCurrentNum - bmiStartNum;
      const bmiIsImproved =
        targetVal < startVal
          ? bmiCurrentNum < bmiStartNum
          : bmiCurrentNum > bmiStartNum;

      bmiInfo = {
        start: startBmi,
        current: currentBmi,
        target: targetBmi,
        category,
        categoryColor,
        categoryBg,
        bmiPct,
        diff: bmiDiff,
        isImproved: bmiIsImproved,
        hasHistory: vals.length > 1,
      };
    }
  }

  // Pace & projection calculations
  const metricLogs = useMemo(() => {
    const logs = [];
    const sDate = startDate ? new Date(startDate) : null;
    if (sDate) {
      logs.push({ date: sDate, val: startVal });
    }
    const sorted = [...(entries || [])].sort((a, b) => {
      const da = new Date(a.recordedDate || a.date || a.createdAt || 0);
      const db = new Date(b.recordedDate || b.date || b.createdAt || 0);
      return da - db;
    });
    sorted.forEach((e) => {
      const pm = e.measurements?.find((p) => p.metric === m.metric);
      if (pm && !isNaN(parseFloat(pm.value))) {
        logs.push({
          date: new Date(e.recordedDate || e.date || e.createdAt),
          val: parseFloat(pm.value),
        });
      }
    });
    return logs;
  }, [entries, startDate, m.metric, startVal]);

  const paceInfo = useMemo(() => {
    if (metricLogs.length < 2) return null;
    const firstLog = metricLogs[0];
    const latestLog = metricLogs[metricLogs.length - 1];
    const timeDiff = latestLog.date - firstLog.date;
    const daysElapsed = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    if (daysElapsed < 5) return null;

    const totalChange = latestLog.val - firstLog.val;
    const ratePerDay = Math.abs(totalChange) / daysElapsed;
    const ratePerWeek = ratePerDay * 7;
    if (ratePerWeek < 0.05) return null;

    const remainingToGoal = Math.abs(targetVal - latestLog.val);
    const daysToGoal = remainingToGoal / ratePerDay;
    const projectedDate = new Date(
      latestLog.date.getTime() + daysToGoal * 24 * 3600 * 1000,
    );

    return {
      ratePerWeek,
      projectedDate,
      isLoss: targetVal < startVal,
    };
  }, [metricLogs, targetVal, startVal]);

  const isLoss = targetVal < startVal;
  const action =
    diff < 0
      ? m.metric === "weight"
        ? "lost"
        : "reduced"
      : m.metric === "weight"
        ? "gained"
        : "increased";

  return (
    <div className="p-3 bg-secondary/15 rounded-xl border border-border/40 hover:border-border/80 transition-all duration-300 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase font-bold tracking-wider text-muted-foreground">
          {info.label}
        </span>
        {vals.length > 1 && <Sparkline values={vals} color="#6366f1" />}
      </div>

      <div className="grid grid-cols-3 gap-2.5 items-stretch">
        {/* Start Card */}
        <div className="bg-background/40 p-2.5 rounded-xl border border-border/20 text-center flex flex-col justify-center">
          <p className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground/60 font-semibold">
            Start {info.label}
          </p>
          <p className="text-xs sm:text-sm font-bold text-muted-foreground/85 mt-1 font-mono">
            {m.currentValue} {info.unit}
          </p>
        </div>

        {/* Current Card (Larger, Elevated) */}
        <div className="bg-primary/10 backdrop-blur-sm border border-primary/30 shadow-md p-2.5 rounded-xl text-center relative -my-1 scale-[1.05] ring-2 ring-primary/5 flex flex-col justify-center">
          <p className="text-[9px] font-mono uppercase tracking-wider text-primary font-bold">
            Current {info.label}
          </p>
          <p className="text-base sm:text-lg font-black font-heading mt-0.5 text-foreground leading-none font-mono">
            {latestValue} {info.unit}
          </p>
          {vals.length > 1 && diff !== 0 && (
            <div className="mt-1">
              <span
                className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isImproved ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-400"}`}
              >
                {diff < 0 ? "↓" : "↑"}{" "}
                {Math.abs(diff).toFixed(1).replace(/\.0$/, "")} {info.unit}{" "}
                {action}
              </span>
            </div>
          )}

          {paceInfo && (
            <div className="mt-2 text-[7.5px] font-mono text-muted-foreground/90 border-t border-border/10 pt-1.5 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-[6.5px] font-bold uppercase text-muted-foreground/60 tracking-wider">
                  Pace
                </span>
                <span className="font-bold text-foreground text-[8px]">
                  {paceInfo.isLoss ? "↓" : "↑"}{" "}
                  {paceInfo.ratePerWeek.toFixed(1)} {info.unit}/wk
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[6.5px] font-bold uppercase text-muted-foreground/60 tracking-wider">
                  Proj. Goal
                </span>
                <span className="font-bold text-primary text-[8px]">
                  {paceInfo.projectedDate.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
        {/* Target Card */}
        <div className="bg-background/40 p-2.5 rounded-xl border border-border/20 text-center flex flex-col justify-center">
          <p className="text-[8px] font-mono uppercase tracking-wider text-primary/70 font-bold">
            Target {info.label}
          </p>
          <p className="text-xs sm:text-sm font-bold text-primary/85 mt-1 font-mono">
            {m.targetValue} {info.unit}
          </p>
        </div>
      </div>
    </div>
  );
}

const renderTooltipOffsets = (val, startVal, targetVal, unit) => {
  if (startVal === null || targetVal === null) return null;
  const startDiff = val - startVal;
  const goalDiff = val - targetVal;
  const isLossGoal = targetVal < startVal;

  let startText = "";
  let startColor = "text-muted-foreground";
  if (startDiff !== 0) {
    const isImproved = isLossGoal ? startDiff < 0 : startDiff > 0;
    const directionStr = startDiff < 0 ? "↓" : "↑";
    startText = `${directionStr} ${Math.abs(startDiff).toFixed(1).replace(/\.0$/, "")} ${unit} from start`;
    startColor = isImproved ? "text-green-500" : "text-red-400";
  } else {
    startText = `● Start weight`;
  }

  let goalText = "";
  let goalColor = "text-muted-foreground";
  if (goalDiff !== 0) {
    const isGoalAchieved = isLossGoal ? goalDiff <= 0 : goalDiff >= 0;
    if (isGoalAchieved) {
      goalText = `🎯 Goal reached! (${Math.abs(goalDiff).toFixed(1).replace(/\.0$/, "")} ${unit} past goal)`;
      goalColor = "text-green-500";
    } else {
      const directionStr = goalDiff > 0 ? "↑" : "↓";
      const relationStr = goalDiff > 0 ? "above" : "below";
      goalText = `${directionStr} ${Math.abs(goalDiff).toFixed(1).replace(/\.0$/, "")} ${unit} ${relationStr} goal`;
      goalColor = "text-amber-500";
    }
  } else {
    goalText = `🎯 Goal reached!`;
    goalColor = "text-green-500";
  }

  return (
    <div className="mt-2 pt-1.5 border-t border-border/20 text-xs flex flex-col gap-0.5 select-none font-bold text-left leading-tight">
      <span className={startColor}>{startText}</span>
      <span className={goalColor}>{goalText}</span>
    </div>
  );
};

const PremiumTooltip = ({
  active,
  payload,
  label,
  primaryStart,
  primaryTarget,
}) => {
  if (active && payload && payload.length) {
    const rawDate = payload[0].payload.rawDate;
    const formattedFullDate = rawDate
      ? new Date(rawDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : label;

    return (
      <div className="bg-card/95 backdrop-blur-md border border-border/90 p-3 rounded-xl shadow-xl font-mono min-w-[120px] select-none z-50">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold text-center">
          {formattedFullDate}
        </p>
        <div className="mt-1.5 space-y-0.5 text-center">
          {payload.map((item, idx) => {
            const key = (item.dataKey || item.name || "")
              .toString()
              .toLowerCase();
            const info = MEASUREMENT_INFO[key];
            const unit = info?.unit || "";
            const val = parseFloat(item.value);
            return (
              <div key={idx}>
                <p className="text-sm font-black text-foreground">
                  {item.value} {unit}
                </p>
                {renderTooltipOffsets(val, primaryStart, primaryTarget, unit)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

// ─── GoalCard ────────────────────────────────────────────────────────────────
function GoalCard({
  goal,
  allowMembersToEditGoals,
  allowMembersToAddProgress,
  memberHeight,
  workoutData,
  onRefresh,
}) {
  const [logOpen, setLogOpen] = useState(false);
  const [editGoalOpen, setEditGoalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const c = goalColor(goal.goalType);
  const pct = Math.min(Math.max(goal.progressPercent || 0, 0), 100);
  const isActive = goal.status === "Active";
  const entries = goal.entries || [];

  const [activeMetric, setActiveMetric] = useState(
    goal.measurements?.[0]?.metric || "",
  );
  useEffect(() => {
    if (goal.measurements?.length > 0) {
      setActiveMetric(goal.measurements[0].metric);
    }
  }, [goal]);

  const primaryMeasurement =
    goal.measurements?.find((m) => m.metric === activeMetric) ||
    goal.measurements?.[0];
  const primaryMetric = primaryMeasurement?.metric;
  const primaryStart = primaryMeasurement
    ? parseFloat(primaryMeasurement.currentValue)
    : null;
  const primaryTarget = primaryMeasurement
    ? parseFloat(primaryMeasurement.targetValue)
    : null;
  const primaryInfo = primaryMetric ? MEASUREMENT_INFO[primaryMetric] : null;
  const primaryUnit = primaryInfo?.unit || "";

  const primaryLatest = useMemo(() => {
    if (!primaryMetric || !entries.length) return primaryStart;
    const sorted = [...entries].sort((a, b) => {
      const da = new Date(a.recordedDate || a.date || a.createdAt || 0);
      const db = new Date(b.recordedDate || b.date || b.createdAt || 0);
      return da - db;
    });
    const last = sorted[sorted.length - 1];
    const pm = last.measurements?.find((p) => p.metric === primaryMetric);
    return pm ? parseFloat(pm.value) : primaryStart;
  }, [entries, primaryMetric, primaryStart]);

  const [animatedPct, setAnimatedPct] = useState(0);

  const primaryPct = useMemo(() => {
    if (
      primaryStart === null ||
      primaryLatest === null ||
      primaryTarget === null
    )
      return 0;
    const totalDiff = primaryTarget - primaryStart;
    const currentDiff = primaryLatest - primaryStart;
    if (totalDiff === 0) return primaryLatest === primaryTarget ? 100 : 0;
    return Math.min(Math.max((currentDiff / totalDiff) * 100, 0), 100);
  }, [primaryStart, primaryLatest, primaryTarget]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAnimatedPct(primaryPct);
    }, 100);
    return () => clearTimeout(t);
  }, [primaryPct]);

  const isWeight = primaryMetric === "weight";
  const bmiInfo = useMemo(() => {
    if (
      !isWeight ||
      !memberHeight ||
      primaryStart === null ||
      primaryLatest === null
    )
      return null;
    const heightNum = parseFloat(memberHeight);
    if (heightNum <= 0) return null;

    const hM = heightNum / 100;
    const startBmi = (primaryStart / (hM * hM)).toFixed(1);
    const currentBmi = (primaryLatest / (hM * hM)).toFixed(1);
    const targetBmi =
      primaryTarget > 0 ? (primaryTarget / (hM * hM)).toFixed(1) : null;

    let category = "";
    let categoryColor = "";
    let categoryBg = "";
    const bmiVal = parseFloat(currentBmi);

    if (bmiVal < 18.5) {
      category = "Underweight";
      categoryColor = "text-blue-500 dark:text-blue-400";
      categoryBg = "bg-blue-500/10";
    } else if (bmiVal < 25.0) {
      category = "Normal";
      categoryColor = "text-green-500 dark:text-green-400";
      categoryBg = "bg-green-500/10";
    } else if (bmiVal < 30.0) {
      category = "Overweight";
      categoryColor = "text-amber-500 dark:text-amber-400";
      categoryBg = "bg-amber-500/10";
    } else {
      category = "Obese";
      categoryColor = "text-red-500 dark:text-red-400";
      categoryBg = "bg-red-500/10";
    }

    const bmiPct = Math.min(
      Math.max(((bmiVal - 15) / (35 - 15)) * 100, 0),
      100,
    );

    const bmiStartNum = parseFloat(startBmi) || 0;
    const bmiCurrentNum = parseFloat(currentBmi) || 0;
    const bmiDiff = bmiCurrentNum - bmiStartNum;
    const bmiIsImproved =
      primaryTarget < primaryStart
        ? bmiCurrentNum < bmiStartNum
        : bmiCurrentNum > bmiStartNum;

    return {
      start: startBmi,
      current: currentBmi,
      target: targetBmi,
      category,
      categoryColor,
      categoryBg,
      bmiPct,
      diff: bmiDiff,
      isImproved: bmiIsImproved,
      hasHistory: entries.length > 1,
    };
  }, [
    isWeight,
    memberHeight,
    primaryStart,
    primaryLatest,
    primaryTarget,
    entries,
  ]);

  const stats = useMemo(() => {
    const logsCount = entries.length;
    if (
      !workoutData ||
      !workoutData.schedule ||
      workoutData.schedule.length === 0
    ) {
      return {
        streak: 0,
        logsCount,
        attendance: 0,
        completedCount: 0,
        totalCount: 0,
      };
    }
    const schedules = workoutData.schedule || [];
    const todayStr = new Date().toISOString().slice(0, 10);
    const pastNonRest = schedules.filter(
      (s) => s.date <= todayStr && s.phase !== "rest",
    );
    const completedCount = pastNonRest.filter(
      (s) => s.status === "completed",
    ).length;
    const totalCount = pastNonRest.length;
    const attendance =
      totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    const sortedPast = [...pastNonRest].sort((a, b) =>
      b.date.localeCompare(a.date),
    );
    let streak = 0;
    for (const s of sortedPast) {
      if (s.status === "completed") {
        streak++;
      } else {
        break;
      }
    }
    return { streak, logsCount, attendance, completedCount, totalCount };
  }, [workoutData, entries]);

  const projectedGoalDate = useMemo(() => {
    if (
      !primaryMetric ||
      entries.length < 2 ||
      primaryStart === null ||
      primaryTarget === null
    ) {
      return goal.targetDate
        ? new Date(goal.targetDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : null;
    }
    const sorted = [...entries].sort((a, b) => {
      const da = new Date(a.recordedDate || a.date || a.createdAt || 0);
      const db = new Date(b.recordedDate || b.date || b.createdAt || 0);
      return da - db;
    });
    const firstLog = {
      date: goal.startDate ? new Date(goal.startDate) : new Date(),
      val: primaryStart,
    };
    const latestLog = {
      date: new Date(
        sorted[sorted.length - 1].recordedDate ||
          sorted[sorted.length - 1].date ||
          sorted[sorted.length - 1].createdAt,
      ),
      val: primaryLatest,
    };
    const timeDiff = latestLog.date - firstLog.date;
    const daysElapsed = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    if (daysElapsed < 7) {
      return goal.targetDate
        ? new Date(goal.targetDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : null;
    }
    const totalChange = latestLog.val - firstLog.val;
    const isWeightLoss = primaryTarget < primaryStart;
    const improved = isWeightLoss
      ? latestLog.val < primaryStart
      : latestLog.val > primaryStart;
    if (!improved) {
      return goal.targetDate
        ? new Date(goal.targetDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : null;
    }
    const ratePerDay = Math.abs(totalChange) / daysElapsed;
    if (ratePerDay <= 0.001) {
      return goal.targetDate
        ? new Date(goal.targetDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : null;
    }
    const remainingToGoal = Math.abs(primaryTarget - latestLog.val);
    const daysToGoal = remainingToGoal / ratePerDay;
    const projDate = new Date(
      latestLog.date.getTime() + daysToGoal * 24 * 3600 * 1000,
    );
    return projDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [
    entries,
    goal.startDate,
    goal.targetDate,
    primaryMetric,
    primaryStart,
    primaryTarget,
    primaryLatest,
  ]);

  const chartData = useMemo(() => {
    if (!entries.length) return [];
    return [...entries]
      .sort((a, b) => {
        const da = new Date(a.recordedDate || a.date || a.createdAt || 0);
        const db = new Date(b.recordedDate || b.date || b.createdAt || 0);
        return da - db;
      })
      .map((e) => {
        const rawDate = e.recordedDate || e.date || e.createdAt || "";
        let formattedDate = "";
        try {
          formattedDate = rawDate
            ? new Date(rawDate).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
              })
            : "";
        } catch {
          formattedDate = rawDate ? rawDate.slice(5, 10) : "";
        }
        const dp = { date: formattedDate, rawDate };
        (e.measurements || []).forEach((m) => {
          dp[m.metric] = m.value;
        });
        return dp;
      });
  }, [entries]);

  const metrics = useMemo(() => {
    return goal.measurements?.map((m) => m.metric) || [];
  }, [goal.measurements]);

  const chronological = useMemo(() => {
    return [...entries].sort((a, b) => {
      const da = new Date(a.recordedDate || a.date || a.createdAt || 0);
      const db = new Date(b.recordedDate || b.date || b.createdAt || 0);
      return da - db;
    });
  }, [entries]);

  // group entries by metric for sparklines
  const byMetric = {};
  entries.forEach((e) => {
    (e.measurements || []).forEach((m) => {
      if (!byMetric[m.metric]) byMetric[m.metric] = [];
      byMetric[m.metric].push(m.value);
    });
  });

  return (
    <>
      <Card
        className={`bg-card border-border ${isActive ? "ring-1 ring-primary/30" : ""}`}
      >
        <CardContent className="p-4 space-y-4">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{goal.name}</p>
              <span
                className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 ${c.bg} ${c.text}`}
              >
                {(goal.goalType || "goal").replace(/_/g, " ")}
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
                isActive
                  ? "bg-green-500/10 text-green-500"
                  : "bg-secondary text-muted-foreground"
              }`}
            >
              {isActive ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <Clock className="h-3 w-3" />
              )}
              {goal.status}
            </span>
          </div>

          {/* Actions row */}
          <div className="flex items-center justify-between border-t border-border/10 pt-2.5">
            <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider text-[10px]">
              Goal Actions
            </span>
            <div className="flex gap-2 shrink-0">
              {isActive &&
                (goal.allowMemberToRecord || allowMembersToAddProgress) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-[11px] font-heading uppercase tracking-wide rounded-lg flex items-center gap-1 shrink-0 bg-primary/5 border-primary/20 text-primary hover:bg-primary/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEntry(null);
                      setLogOpen(true);
                    }}
                    data-testid="log-progress-btn"
                  >
                    <Plus className="h-3.5 w-3.5" /> Log Progress
                  </Button>
                )}
              {isActive && allowMembersToEditGoals && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-[11px] font-heading uppercase tracking-wide rounded-lg flex items-center gap-1 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditGoalOpen(true);
                  }}
                  data-testid="edit-goal-btn"
                >
                  <Edit2 className="h-3.5 w-3.5" /> Edit Goal
                </Button>
              )}
            </div>
          </div>

          {/* Summary Hero Card */}
          {primaryMeasurement && (
            <div className="bg-gradient-to-br from-indigo-500/15 via-indigo-500/5 to-transparent border-2 border-indigo-500/30 shadow-lg p-5 rounded-2xl relative overflow-hidden space-y-4">
              <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/10 rounded-full filter blur-3xl pointer-events-none" />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎯</span>
                  <span className=" font-black text-sm uppercase tracking-wider text-foreground">
                    Goal Overview
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className="text-[8px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 border-indigo-500/30 text-indigo-500 dark:text-indigo-400 bg-indigo-500/10"
                >
                  {(goal.goalType || "goal").replace(/_/g, " ")}
                </Badge>
              </div>

              {/* Pill tabs for switching between tracked measurements */}
              {goal.measurements?.length > 1 && (
                <div className="flex flex-wrap gap-1.5 border-b border-border/10 pb-3">
                  {goal.measurements.map((m) => {
                    const info = MEASUREMENT_INFO[m.metric];
                    const label = info?.label || m.metric.replace(/_/g, " ");
                    const isSelected = activeMetric === m.metric;
                    return (
                      <button
                        key={m.metric}
                        type="button"
                        onClick={() => setActiveMetric(m.metric)}
                        className={`px-3 py-1 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider transition-all duration-200 select-none cursor-pointer ${
                          isSelected
                            ? "bg-indigo-600 text-white dark:bg-indigo-500 shadow-sm ring-1 ring-indigo-500/20 scale-[1.03]"
                            : "bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-border/10"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* 3-column start/current/target card grid */}
              <div className="grid grid-cols-3 gap-2.5 items-stretch">
                {/* Start Card */}
                <div className="bg-background/40 p-2.5 rounded-xl border border-border/20 text-center flex flex-col justify-center">
                  <span className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground/60 font-semibold">
                    Start
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-muted-foreground/85 mt-1 font-mono">
                    {primaryStart}
                    {primaryUnit}
                  </span>
                </div>

                {/* Current Card (Larger, Elevated) */}
                <div className="bg-indigo-500/15 backdrop-blur-sm border-2 border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.25)] p-3 rounded-2xl text-center relative -my-1 scale-[1.08] ring-4 ring-indigo-500/5 flex flex-col justify-center">
                  <span className="text-[9.5px] uppercase font-mono tracking-wider text-indigo-500 dark:text-indigo-400 font-bold">
                    Current
                  </span>
                  <span className="text-lg sm:text-2xl font-black text-foreground mt-0.5 leading-none font-mono">
                    {primaryLatest}
                    {primaryUnit}
                  </span>
                  {primaryLatest !== primaryStart && (
                    <span
                      className={`text-[8.5px] sm:text-[9.5px] font-black mt-1.5 select-none whitespace leading-none ${
                        primaryTarget < primaryStart
                          ? primaryLatest < primaryStart
                            ? "text-green-500"
                            : "text-red-400"
                          : primaryLatest > primaryStart
                            ? "text-green-500"
                            : "text-red-400"
                      }`}
                    >
                      {primaryTarget < primaryStart ? "↓" : "↑"}{" "}
                      {Math.abs(primaryLatest - primaryStart).toFixed(1)}
                      {primaryUnit}{" "}
                      {primaryTarget < primaryStart ? "lost" : "gained"}
                    </span>
                  )}
                </div>

                {/* Target Card */}
                <div className="bg-background/40 p-2.5 rounded-xl border border-border/20 text-center flex flex-col justify-center">
                  <span className="text-[8px] font-mono uppercase tracking-wider text-indigo-500/70 dark:text-indigo-400/70 font-bold">
                    Target
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-indigo-500/85 dark:text-indigo-400/85 mt-1 font-mono">
                    {primaryTarget}
                    {primaryUnit}
                  </span>
                </div>
              </div>

              {/* 3-column stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-background/40 backdrop-blur-sm border border-border/30 p-3 rounded-xl text-center flex flex-col justify-center items-center shadow-sm">
                  <span className="text-[9px] uppercase font-mono tracking-wider text-muted-foreground font-semibold">
                    Change
                  </span>
                  <div className="mt-1 flex items-center justify-center gap-0.5">
                    <div
                      className={`text-sm sm:text-base font-black ${primaryLatest !== primaryStart ? (primaryTarget < primaryStart ? (primaryLatest < primaryStart ? "text-green-500" : "text-red-400") : primaryLatest > primaryStart ? "text-green-500" : "text-red-400") : "text-muted-foreground"}`}
                    >
                      {primaryLatest !== primaryStart ? (
                        <>
                          {primaryTarget < primaryStart ? "↓ Lost" : "↑ Gained"}
                          <span className="block text-base sm:text-lg font-black mt-0.5">
                            {Math.abs(primaryLatest - primaryStart).toFixed(1)}{" "}
                            <span className="text-[10px] font-normal normal-case">
                              {primaryUnit}
                            </span>
                          </span>
                        </>
                      ) : (
                        "No change"
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-background/40 backdrop-blur-sm border border-border/30 p-3 rounded-xl text-center flex flex-col justify-center items-center shadow-sm">
                  <span className="text-[9px] uppercase font-mono tracking-wider text-indigo-500 dark:text-indigo-400 font-bold">
                    Progress
                  </span>
                  <p className="text-base sm:text-lg font-black text-indigo-500 dark:text-indigo-400 mt-1">
                    {primaryPct.toFixed(0)}%
                    <span className="block text-[9px] font-mono font-semibold text-muted-foreground/70 mt-0.5">
                      Complete
                    </span>
                  </p>
                </div>

                <div className="bg-background/40 backdrop-blur-sm border border-border/30 p-3 rounded-xl text-center flex flex-col justify-center items-center shadow-sm">
                  <span className="text-[9px] uppercase font-mono tracking-wider text-muted-foreground font-semibold">
                    Remaining
                  </span>
                  <p className="text-base sm:text-lg font-black text-foreground mt-1">
                    {primaryTarget !== null && primaryLatest !== null
                      ? Math.abs(primaryTarget - primaryLatest).toFixed(1)
                      : 0}
                    <span className="text-[10px] font-normal normal-case ml-0.5">
                      {primaryUnit}
                    </span>
                    <span className="block text-[9px] font-mono font-semibold text-muted-foreground/70 mt-0.5">
                      Remaining
                    </span>
                  </p>
                </div>
              </div>

              {/* Progress bar and details */}
              <div className="space-y-1.5 pt-2 border-t border-border/10">
                <div className="h-2.5 bg-secondary/50 rounded-full overflow-hidden relative border border-border/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 transition-all duration-1000 ease-out"
                    style={{ width: `${animatedPct}%` }}
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-between text-[9px] font-mono text-muted-foreground px-0.5 gap-1 pt-1">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="font-bold text-foreground text-[10px]">
                      {primaryTarget !== primaryStart
                        ? `${primaryTarget < primaryStart ? "Lost" : "Gained"} ${Math.abs(primaryLatest - primaryStart).toFixed(1)} ${primaryUnit} of ${Math.abs(primaryTarget - primaryStart).toFixed(1)} ${primaryUnit} goal`
                        : "Goal completed"}
                    </span>
                    <span>•</span>
                    <span className="font-bold text-indigo-500 dark:text-indigo-400 text-[10px]">
                      {primaryPct.toFixed(0)}% Complete
                    </span>
                  </div>
                  <span className="font-bold text-amber-500 shrink-0 text-[12px]">
                    {primaryTarget !== primaryStart
                      ? `${Math.abs(primaryLatest - primaryTarget).toFixed(1)} ${primaryUnit} remaining`
                      : `0 ${primaryUnit} remaining`}
                  </span>
                </div>
              </div>

              {projectedGoalDate && (
                <div className="text-[10px] font-mono text-muted-foreground flex justify-between items-center border-t border-border/10 pt-2.5">
                  <span>Estimated Completion</span>
                  <span className="font-bold text-foreground">
                    {projectedGoalDate}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* BMI Trend Card (placed below Goal Overview) */}
          {isWeight && (
            <Card className="bg-card border-border/85 shadow-sm p-4 rounded-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full filter blur-2xl pointer-events-none" />
              {bmiInfo ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className=" text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                      <span>⚖️</span> BMI Trend
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${bmiInfo.categoryBg} ${bmiInfo.categoryColor}`}
                    >
                      {bmiInfo.category}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 bg-background/40 p-2.5 rounded-lg border border-border/20">
                    <div className="text-center border-r border-border/20">
                      <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                        Start BMI
                      </p>
                      <p className="text-xs sm:text-sm font-black mt-0.5 text-muted-foreground/90 font-mono">
                        {bmiInfo.start || "—"}
                      </p>
                    </div>

                    <div className="text-center border-r border-border/20 relative">
                      <p className="text-[11px] font-mono uppercase tracking-wider  font-bold">
                        Current BMI
                      </p>
                      <p className="text-sm sm:text-base font-black mt-0.5 text-foreground font-mono flex items-center justify-center gap-1">
                        {bmiInfo.current}
                        {bmiInfo.hasHistory && bmiInfo.diff !== 0 && (
                          <span
                            className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded bg-secondary/50 flex items-center ${bmiInfo.isImproved ? "text-green-500" : "text-red-400"}`}
                          >
                            {bmiInfo.diff < 0 ? "↓" : "↑"}
                            {Math.abs(bmiInfo.diff).toFixed(1)}
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="text-center">
                      <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                        Target BMI
                      </p>
                      <p className="text-xs sm:text-sm font-black mt-0.5  font-mono">
                        {bmiInfo.target || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="relative pt-1.5 pb-0.5">
                    <div className="h-1.5 w-full rounded-full bg-gradient-to-r from-blue-400 via-green-500 via-amber-500 to-red-400 opacity-80" />
                    <div
                      className="absolute top-0 -ml-1.5 w-3 h-3 rounded-full bg-foreground border-2 border-background shadow-md transition-all duration-1000 ease-out"
                      style={{ left: `${bmiInfo.bmiPct}%` }}
                    />
                    <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-1">
                      <span>Underweight (&lt;18.5)</span>
                      <span>Normal (18.5-25)</span>
                      <span>Overweight (25-30)</span>
                      <span>Obese (&gt;30)</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-2 bg-secondary/10 rounded border border-dashed border-border/30 text-muted-foreground italic text-[9px]">
                  Set your height in your profile settings to see your BMI
                  gauge.
                </div>
              )}
            </Card>
          )}

          {/* Behavior / Streaks tracking row */}
          {stats && (
            <div className="grid grid-cols-3 bg-secondary/15 border border-border/30 p-2.5 rounded-xl text-center text-xs select-none shadow-sm">
              <div>
                <span className="block text-[10px] text-muted-foreground uppercase font-mono font-semibold tracking-wider">
                  🔥 Streak
                </span>
                <span className="font-black text-foreground text-[11px] mt-0.5 block">
                  {stats.streak} Days
                </span>
              </div>
              <div className="border-x border-border/20">
                <span className="block text-[10px] text-muted-foreground uppercase font-mono font-semibold tracking-wider">
                  ✅ Logged
                </span>
                <span className="font-black text-foreground text-[11px] mt-0.5 block">
                  {stats.logsCount} Entries
                </span>
              </div>
              <div>
                <span className="block text-[10px] text-muted-foreground uppercase font-mono font-semibold tracking-wider">
                  🏋️ Workouts
                </span>
                <span className="font-black text-foreground text-[11px] mt-0.5 block">
                  {stats.completedCount} / {stats.totalCount}
                </span>
              </div>
            </div>
          )}

          {/* History / Analytics container (Stacked Charts) */}
          {entries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border/40 space-y-6">
              {/* 1. Progress Trend */}
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                    Progress Trend
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono select-none">
                    {primaryStart !== null && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <span className="font-semibold text-foreground/80">
                          {primaryStart}
                          {primaryUnit}
                        </span>
                        <span className="text-[10px] text-muted-foreground/60">
                          ● Start
                        </span>
                      </div>
                    )}
                    {primaryLatest !== null && (
                      <div className="flex items-center gap-1 text-foreground">
                        <span className="font-black text-primary">
                          {primaryLatest}
                          {primaryUnit}
                        </span>
                        <span className="text-[10px] text-primary/80">
                          ● Current
                        </span>
                      </div>
                    )}
                    {primaryTarget !== null && (
                      <div className="flex items-center gap-1 text-emerald-500 font-bold">
                        <span>
                          {primaryTarget}
                          {primaryUnit}
                        </span>
                        <span>🎯 Goal</span>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="h-[250px] p-3 border border-border/80 rounded-xl bg-card/40 backdrop-blur-sm shadow-sm relative overflow-hidden"
                  data-testid="member-progress-graph-container"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 15, right: 15, left: -25, bottom: 5 }}
                    >
                      <defs>
                        <filter
                          id="glow"
                          x="-10%"
                          y="-10%"
                          width="120%"
                          height="120%"
                        >
                          <feGaussianBlur stdDeviation="2.5" result="blur" />
                          <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                        {metrics.map((metric, idx) => {
                          const colors = [
                            "rgb(99, 102, 241)", // Indigo
                            "rgb(16, 185, 129)", // Emerald
                            "rgb(245, 158, 11)", // Amber
                            "rgb(236, 72, 153)", // Pink
                            "rgb(139, 92, 246)", // Purple
                          ];
                          const color = colors[idx % colors.length];
                          return (
                            <linearGradient
                              key={metric}
                              id={`color-${metric}`}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor={color}
                                stopOpacity={0.25}
                              />
                              <stop
                                offset="100%"
                                stopColor={color}
                                stopOpacity={0.0}
                              />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      <CartesianGrid
                        vertical={false}
                        stroke="rgba(148, 163, 184, 0.08)"
                      />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        className="text-[9px] font-mono fill-muted-foreground/60"
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        className="text-[9px] font-mono fill-muted-foreground/60"
                      />
                      <ChartTooltip
                        content={
                          <PremiumTooltip
                            primaryStart={primaryStart}
                            primaryTarget={primaryTarget}
                          />
                        }
                      />
                      {primaryLatest !== null && (
                        <ReferenceLine
                          y={primaryLatest}
                          stroke="hsl(var(--primary))"
                          strokeDasharray="3 3"
                          strokeWidth={1.5}
                        />
                      )}
                      {primaryTarget !== null && (
                        <ReferenceLine
                          y={primaryTarget}
                          stroke="#10b981"
                          strokeWidth={2}
                        />
                      )}
                      {metrics.map((metric, idx) => {
                        const colors = [
                          "#6366f1",
                          "#10b981",
                          "#f59e0b",
                          "#ec4899",
                          "#8b5cf6",
                        ];
                        return (
                          <Area
                            key={metric}
                            type="monotone"
                            dataKey={metric}
                            name={
                              metric.charAt(0).toUpperCase() + metric.slice(1)
                            }
                            stroke={colors[idx % colors.length]}
                            fill={`url(#color-${metric})`}
                            activeDot={{ r: 5, strokeWidth: 0 }}
                            strokeWidth={2.5}
                            filter="url(#glow)"
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 4. Logs History (Bottom) */}
              <div className="space-y-2 pt-4 border-t border-border/20">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold">
                  Logs History
                </p>
                <div className="border border-border/80 rounded-xl overflow-hidden max-h-[250px] overflow-y-auto shadow-sm">
                  {[...entries]
                    .sort(
                      (a, b) =>
                        new Date(b.recordedDate) - new Date(a.recordedDate),
                    )
                    .map((e, i) => (
                      <div
                        key={i}
                        className="px-3 py-2.5 border-b border-border last:border-0 bg-secondary/20 hover:bg-secondary/30 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs font-semibold text-foreground">
                              {e.recordedDate
                                ? new Date(e.recordedDate).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )
                                : "—"}
                            </p>
                            {e.creatorName && (
                              <span className="text-[9px] bg-secondary/80 px-1.5 py-0.5 rounded border border-border/40 text-muted-foreground font-medium">
                                by {e.creatorName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isActive &&
                              (goal.allowMemberToRecord ||
                                allowMembersToAddProgress) &&
                              e.createdBy &&
                              e.createdBy.startsWith("member:") && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1.5 text-[10px] font-semibold text-primary hover:underline uppercase tracking-wide flex items-center gap-1"
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    setSelectedEntry(e);
                                    setLogOpen(true);
                                  }}
                                  data-testid="edit-progress-btn"
                                >
                                  <Edit2 className="h-3 w-3" /> Edit
                                </Button>
                              )}
                            {e.notes && (
                              <p className="text-[10px] text-muted-foreground italic truncate max-w-[120px]">
                                {e.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(e.measurements || []).map((m, mi) => {
                            const info = MEASUREMENT_INFO[m.metric];
                            const unit = info?.unit || "";

                            const idx = chronological.findIndex(
                              (x) =>
                                (x.recordedDate || x.date || x.createdAt) ===
                                (e.recordedDate || e.date || e.createdAt),
                            );

                            let diffText = "";
                            let diffBg = "";
                            if (idx > 0) {
                              const prevEntry = chronological[idx - 1];
                              const prevM = prevEntry.measurements?.find(
                                (p) => p.metric === m.metric,
                              );
                              if (prevM) {
                                const curVal = parseFloat(m.value);
                                const prevVal = parseFloat(prevM.value);
                                const diff = curVal - prevVal;
                                if (!isNaN(diff) && diff !== 0) {
                                  const mGoal = goal.measurements?.find(
                                    (x) => x.metric === m.metric,
                                  );
                                  const mStart = mGoal
                                    ? parseFloat(mGoal.currentValue)
                                    : null;
                                  const mTarget = mGoal
                                    ? parseFloat(mGoal.targetValue)
                                    : null;
                                  const isLossGoal =
                                    mStart !== null && mTarget !== null
                                      ? mTarget < mStart
                                      : false;
                                  const improved =
                                    m.metric === "weight"
                                      ? isLossGoal
                                        ? diff < 0
                                        : diff > 0
                                      : diff > 0;

                                  diffText = `${diff > 0 ? "↑" : "↓"} ${Math.abs(diff).toFixed(1).replace(/\.0$/, "")} ${unit}`;
                                  diffBg = improved
                                    ? "bg-green-500/15 text-green-500 dark:text-green-400 border border-green-500/30"
                                    : "bg-red-500/15 text-red-500 dark:text-red-400 border border-red-500/30";
                                }
                              }
                            }

                            return (
                              <div
                                key={mi}
                                className="flex items-center gap-2 text-[10px] font-mono bg-background/35 px-2.5 py-1 rounded-xl border border-border/20 shadow-sm mt-1"
                              >
                                <span className="uppercase text-[8px] font-mono text-muted-foreground/60 font-semibold tracking-wider">
                                  {m.metric.replace(/_/g, " ")}
                                </span>
                                <span className="font-black text-foreground text-xs">
                                  {m.value}
                                  <span className="text-[9px] text-muted-foreground font-normal ml-0.5">
                                    {unit}
                                  </span>
                                </span>
                                {diffText ? (
                                  <span
                                    className={`text-[9px] px-1.5 py-0.5 rounded-md font-bold ${diffBg}`}
                                  >
                                    {diffText}
                                  </span>
                                ) : (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-secondary/30 text-muted-foreground/50 border border-border/10 font-medium">
                                    Baseline
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isActive && (goal.allowMemberToRecord || allowMembersToAddProgress) && (
        <AddMemberProgressModal
          open={logOpen}
          onOpenChange={setLogOpen}
          goal={goal}
          existingEntry={selectedEntry}
          onSuccess={onRefresh}
        />
      )}
      {isActive && allowMembersToEditGoals && (
        <EditMemberGoalModal
          open={editGoalOpen}
          onOpenChange={setEditGoalOpen}
          goal={goal}
          onSuccess={onRefresh}
        />
      )}
      {isActive && (goal.allowMemberToRecord || allowMembersToAddProgress) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedEntry(null);
            setLogOpen(true);
          }}
          className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px)+1rem)] right-6 z-40 md:bottom-8 md:right-8 flex items-center gap-1.5 bg-primary text-primary-foreground font-black text-[11px] uppercase tracking-wider px-4 py-3 rounded-full shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/40 hover:scale-105 active:scale-95 transition-all duration-300 border border-primary/20 cursor-pointer"
          data-testid="floating-log-progress-btn"
        >
          <Plus className="h-4 w-4" />
          <span>Log Progress</span>
        </button>
      )}
    </>
  );
}

function AddMemberProgressModal({
  open,
  onOpenChange,
  goal,
  onSuccess,
  existingEntry = null,
}) {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (goal?.measurements) {
      const initial = {};
      if (existingEntry) {
        (existingEntry.measurements || []).forEach((m) => {
          initial[m.metric] = m.value;
        });
        setNotes(existingEntry.notes || "");
      } else {
        goal.measurements.forEach((m) => {
          let latestVal = m.currentValue;
          if (goal.entries?.length > 0) {
            const lastEntry = goal.entries[0];
            const lastM = lastEntry.measurements?.find(
              (pm) => pm.metric === m.metric,
            );
            if (lastM) latestVal = lastM.value;
          }
          initial[m.metric] = latestVal;
        });
        setNotes("");
      }
      setValues(initial);
    }
  }, [goal, open, existingEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("member_token");
      const payload = {
        goalId: goal.id,
        notes,
        measurements: Object.entries(values).map(([metric, value]) => ({
          metric,
          value: parseFloat(value) || 0,
        })),
      };
      if (existingEntry) {
        await api.put(`/member-portal/progress/${existingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Progress updated successfully!");
      } else {
        await api.post("/member-portal/progress", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Progress logged successfully!");
      }
      window.dispatchEvent(new CustomEvent("progressLogged"));
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save progress");
    } finally {
      setLoading(false);
    }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {existingEntry ? "Edit Progress" : "Log Progress"}
          </DialogTitle>
          <DialogDescription className="text-left">
            {existingEntry
              ? `Edit your measurements for goal: ${goal.name}`
              : `Enter your current measurements for goal: ${goal.name}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {goal.measurements?.map((m) => {
            const label = m.metric.charAt(0).toUpperCase() + m.metric.slice(1);
            return (
              <div key={m.metric} className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground flex justify-between">
                  <span>{label}</span>
                  <span className="text-[10px] font-sans normal-case">
                    Target: {m.targetValue}
                  </span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={values[m.metric] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [m.metric]: e.target.value })
                  }
                  placeholder={`Enter current ${m.metric}...`}
                  required
                  inputMode="decimal"
                />
              </div>
            );
          })}

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Notes (Optional)
            </Label>
            <Textarea
              placeholder="How are you feeling today? Any details to add..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11 uppercase tracking-wider text-xs font-semibold cursor-pointer"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11  uppercase tracking-wider text-xs font-semibold cursor-pointer"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : existingEntry
                  ? "Update Progress"
                  : "Save Progress"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberGoalModal({ open, onOpenChange, goal, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const GOAL_TYPES = [
    { value: "Weight Loss", label: "Weight Loss" },
    { value: "Weight Gain", label: "Weight Gain" },
    { value: "Muscle Building", label: "Muscle Building" },
    { value: "General Fitness", label: "General Fitness" },
    { value: "Custom", label: "Custom" },
  ];

  const MEASUREMENTS = [
    { id: "weight", label: "Weight", unit: "kg", icon: Scale },
    { id: "chest", label: "Chest", unit: "cm", icon: Ruler },
    { id: "waist", label: "Waist", unit: "cm", icon: Ruler },
    { id: "arm", label: "Arm", unit: "cm", icon: Ruler },
  ];

  const [form, setForm] = useState({
    name: "",
    description: "",
    goalType: "Weight Loss",
    reminderDays: 7,
  });
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);

  useEffect(() => {
    const measurementLabels = selectedMeasurements
      .map((sm) => {
        const mInfo = MEASUREMENTS.find((m) => m.id === sm.metric);
        return mInfo ? mInfo.label : sm.metric;
      })
      .filter(Boolean)
      .join(", ");

    const autoName = measurementLabels
      ? `${form.goalType} (${measurementLabels})`
      : form.goalType;

    setForm((prev) => {
      if (prev.name !== autoName) {
        return { ...prev, name: autoName };
      }
      return prev;
    });
  }, [form.goalType, selectedMeasurements]);

  useEffect(() => {
    if (goal) {
      setForm({
        name: goal.name || "",
        description: goal.description || "",
        goalType: goal.goalType || "Weight Loss",
        reminderDays: goal.reminderDays || 7,
      });
      setSelectedMeasurements(
        goal.measurements?.map((m) => ({
          metric: m.metric,
          currentValue: m.currentValue,
          targetValue: m.targetValue,
        })) || [],
      );
    }
  }, [goal, open]);

  const toggleMeasurement = (metricId) => {
    const exists = selectedMeasurements.find((m) => m.metric === metricId);
    if (exists) {
      setSelectedMeasurements(
        selectedMeasurements.filter((m) => m.metric !== metricId),
      );
    } else {
      setSelectedMeasurements([
        ...selectedMeasurements,
        {
          metric: metricId,
          currentValue: "",
          targetValue: "",
        },
      ]);
    }
  };

  const updateMeasurement = (metric, field, value) => {
    setSelectedMeasurements(
      selectedMeasurements.map((m) =>
        m.metric === metric ? { ...m, [field]: value } : m,
      ),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Goal name is required");
      return;
    }
    if (selectedMeasurements.length === 0) {
      toast.error("Select at least one measurement to track");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("member_token");
      const parsedMeasurements = selectedMeasurements.map((m) => ({
        metric: m.metric,
        currentValue: parseFloat(m.currentValue) || 0,
        targetValue: parseFloat(m.targetValue) || 0,
      }));

      const payload = {
        name: form.name,
        description: form.description,
        goalType: form.goalType,
        reminderDays: form.reminderDays,
        measurements: parsedMeasurements,
      };

      await api.put(`/member-portal/goals/${goal.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Goal updated successfully!");
      window.dispatchEvent(new CustomEvent("progressLogged"));
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update goal");
    } finally {
      setLoading(false);
    }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-4 gap-2"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="pb-3 border-b border-border shrink-0 space-y-0">
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Edit Goal
          </DialogTitle>
          <DialogDescription className="text-left">
            Modify your fitness goal details and targets
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto space-y-4 py-4 px-2"
          data-testid="edit-goal-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 hidden">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Goal Name *
              </Label>
              <Input
                placeholder="e.g., Lose 10kg in 3 months"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Goal Type
              </Label>
              <Select
                value={form.goalType}
                onValueChange={(v) => setForm({ ...form, goalType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Description (Optional)
            </Label>
            <Textarea
              placeholder="Add details about this goal..."
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="min-h-[60px]"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
              Select Measurements to Track
            </Label>
            <div className="flex flex-wrap gap-2">
              {MEASUREMENTS.map((m) => {
                const isSelected = selectedMeasurements.some(
                  (sm) => sm.metric === m.id,
                );
                return (
                  <Badge
                    key={m.id}
                    variant={isSelected ? "default" : "outline"}
                    className={`cursor-pointer px-3 py-1.5 ${isSelected ? "bg-primary" : ""}`}
                    onClick={() => toggleMeasurement(m.id)}
                  >
                    <m.icon className="h-3 w-3 mr-1" />
                    {m.label}
                    {isSelected && <X className="h-3 w-3 ml-1" />}
                  </Badge>
                );
              })}
            </div>
          </div>

          {selectedMeasurements.length > 0 && (
            <div className="space-y-3">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Set Target Values
              </Label>
              <div className="space-y-3">
                {selectedMeasurements.map((sm) => {
                  const measurementInfo = MEASUREMENTS.find(
                    (m) => m.id === sm.metric,
                  ) || {
                    label: MEASUREMENT_INFO[sm.metric]?.label || sm.metric,
                    unit: MEASUREMENT_INFO[sm.metric]?.unit || "",
                    icon: Scale,
                  };
                  return (
                    <div
                      key={sm.metric}
                      className="grid grid-cols-3 gap-3 p-3 bg-secondary/20 rounded-lg"
                    >
                      <div className="col-span-3 sm:col-span-1">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <measurementInfo.icon className="h-4 w-4 text-primary" />
                          {measurementInfo.label}
                        </p>
                        {measurementInfo.unit && (
                          <p className="text-xs text-muted-foreground">
                            Unit: {measurementInfo.unit}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">
                          Current
                        </Label>
                        <Input
                          type="number"
                          step="0.1"
                          inputMode="decimal"
                          value={sm.currentValue}
                          onChange={(e) =>
                            updateMeasurement(
                              sm.metric,
                              "currentValue",
                              e.target.value,
                            )
                          }
                          className="h-9"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">
                          Target
                        </Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.1"
                          value={sm.targetValue}
                          onChange={(e) =>
                            updateMeasurement(
                              sm.metric,
                              "targetValue",
                              e.target.value,
                            )
                          }
                          className="h-9"
                          required
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                Progress Reminder
              </Label>
              <Badge variant="outline" className="font-mono">
                Every {form.reminderDays} days
              </Badge>
            </div>
            <Slider
              value={[form.reminderDays]}
              onValueChange={(v) => setForm({ ...form, reminderDays: v[0] })}
              min={1}
              max={31}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              You'll be reminded to update progress every {form.reminderDays}{" "}
              day{form.reminderDays > 1 ? "s" : ""}
            </p>
          </div>
        </form>

        <div className="pt-4 border-t border-border shrink-0">
          <Button
            type="submit"
            className="w-full h-11 font-heading uppercase tracking-wider"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Goal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function MemberProgress() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [workoutData, setWorkoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active"); // active | all
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        let wrkData = null;
        const [progressRes] = await Promise.all([
          api.get("/member-portal/progress", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api
            .get("/member-portal/workouts", {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
              wrkData = res.data;
            })
            .catch((err) => {
              console.error("Failed to load workouts: ", err);
            }),
        ]);
        setData(progressRes.data);
        setWorkoutData(wrkData);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load progress.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, refreshTrigger]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>{error}</p>
      </div>
    );

  const goals = data?.goals || [];
  const allowMembersToEditGoals = data?.allowMembersToEditGoals || false;
  const allowMembersToAddProgress = data?.allowMembersToAddProgress || false;
  const active = goals.filter((g) => g.status === "Active");
  const completed = goals.filter((g) => g.status !== "Active");
  const totalEntries = goals.reduce((s, g) => s + (g.entries?.length || 0), 0);

  const displayed = tab === "active" ? active : goals;
  const noGoals = goals.length === 0;

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black font-heading uppercase tracking-wide">
          Progress
        </h1>
        <p className="text-muted-foreground text-xs mt-0.5">
          Your fitness goals and measurement history
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatCard
          icon={Target}
          label="Goals"
          value={active.length}
          color="text-green-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Completed"
          value={completed.length}
          color="text-primary"
        />
        <StatCard
          icon={Activity}
          label="Logs"
          value={totalEntries}
          color="text-amber-400"
        />
      </div>

      {/* AI Smart Insights */}
      {active.length > 0 && (
        <ProgressInsightsSection goal={active[0]} workoutData={workoutData} />
      )}

      {noGoals ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
            <TrendingUp className="h-10 w-10 opacity-30" />
            <p className="text-sm">No goals found</p>
            <p className="text-xs text-center max-w-xs">
              Contact the gym staff to set up your fitness goals and start
              tracking your progress.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex w-full rounded-xl overflow-hidden border border-border">
            {[
              {
                key: "active",
                label: `Active (${active.length})`,
                icon: Target,
              },
              {
                key: "all",
                label: `All Goals (${goals.length})`,
                icon: TrendingUp,
              },
            ].map(({ key, label, icon: Icon }, i, arr) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={[
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                  i < arr.length - 1 ? "border-r border-border" : "",
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            ))}
          </div>

          {displayed.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <Ruler className="h-8 w-8 opacity-30" />
                <p className="text-sm">
                  {tab === "active" ? "No active goals" : "No goals found"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              {displayed.map((g, i) => (
                <GoalCard
                  key={i}
                  goal={g}
                  allowMembersToEditGoals={allowMembersToEditGoals}
                  allowMembersToAddProgress={allowMembersToAddProgress}
                  memberHeight={data?.memberHeight}
                  workoutData={workoutData}
                  onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
